import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Loader2, Plus, Sparkles, Check, X, CalendarDays } from "lucide-react";
import api from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { Modal, Input, Select } from "./Projects";
import { priorityAccent, PRIORITY_LABELS } from "../lib/gameData";
import { toast } from "sonner";

const DAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

function startOfWeek(d) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
}
function iso(d) { return d.toISOString().slice(0, 10); }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }

export default function Calendar() {
  const { refresh } = useAuth();
  const [data, setData] = useState(null);
  const [projects, setProjects] = useState([]);
  const [mode, setMode] = useState("week");
  const [ref, setRef] = useState(startOfWeek(new Date()));
  const [creating, setCreating] = useState(null);
  const [optim, setOptim] = useState(null);
  const [dragId, setDragId] = useState(null);

  const load = useCallback(async () => {
    const [c, p] = await Promise.all([api.get("/calendar"), api.get("/projects")]);
    setData(c.data);
    setProjects(p.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 text-amber-500 animate-spin" /></div>;

  const byDate = {};
  data.tasks.forEach((t) => { if (t.deadline) (byDate[t.deadline] ||= []).push(t); });

  const today = data.today;
  let days = [];
  if (mode === "day") days = [new Date(ref)];
  else if (mode === "week") days = Array.from({ length: 7 }, (_, i) => addDays(ref, i));
  else {
    const first = new Date(ref.getFullYear(), ref.getMonth(), 1);
    const gridStart = startOfWeek(first);
    days = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  }

  const shift = (dir) => {
    if (mode === "day") setRef(addDays(ref, dir));
    else if (mode === "week") setRef(addDays(ref, dir * 7));
    else setRef(new Date(ref.getFullYear(), ref.getMonth() + dir, 1));
  };
  const goToday = () => setRef(mode === "month" ? new Date() : startOfWeek(new Date()));

  const onDrop = async (dayIso) => {
    if (!dragId) return;
    const t = data.tasks.find((x) => x.id === dragId);
    setDragId(null);
    if (!t || t.deadline === dayIso) return;
    await api.put(`/tasks/${dragId}`, { deadline: dayIso });
    toast.success(`« ${t.title} » déplacée au ${dayIso}`);
    load();
  };

  const runOptimize = async () => {
    const { data } = await api.get("/schedule/optimize");
    setOptim(data);
  };
  const applyOptimize = async () => {
    const changes = optim.proposal.map((p) => ({ task_id: p.task_id, new_deadline: p.new_deadline }));
    const { data } = await api.post("/schedule/apply", { changes });
    toast.success(data.message);
    setOptim(null);
    load();
  };

  const label = mode === "month"
    ? ref.toLocaleDateString("fr-FR", { month: "long", year: "numeric" })
    : mode === "day"
      ? new Date(ref).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })
      : `${addDays(ref, 0).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })} – ${addDays(ref, 6).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}`;

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
        <div>
          <h1 className="font-display text-3xl font-extrabold">Calendrier</h1>
          <p className="text-zinc-500 text-sm mt-1">Fuseau : {data.timezone}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={runOptimize} data-testid="optimize-btn" className="inline-flex items-center gap-2 px-4 h-10 rounded-xl border border-amber-500/40 text-amber-400 text-sm font-medium hover:bg-amber-500/10">
            <Sparkles className="w-4 h-4" /> Optimiser
          </button>
          <button onClick={() => setCreating({ deadline: today })} data-testid="calendar-new-task" className="inline-flex items-center gap-2 px-4 h-10 rounded-xl gold-gradient text-black font-semibold hover:opacity-90">
            <Plus className="w-4 h-4" /> Tâche
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button onClick={() => shift(-1)} data-testid="cal-prev" className="w-9 h-9 rounded-lg border border-zinc-800 flex items-center justify-center hover:border-amber-500/50"><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={goToday} data-testid="cal-today" className="px-3 h-9 rounded-lg border border-zinc-800 text-sm hover:border-amber-500/50">Aujourd'hui</button>
          <button onClick={() => shift(1)} data-testid="cal-next" className="w-9 h-9 rounded-lg border border-zinc-800 flex items-center justify-center hover:border-amber-500/50"><ChevronRight className="w-4 h-4" /></button>
          <span className="ml-2 text-sm font-medium capitalize">{label}</span>
        </div>
        <div className="flex gap-1 bg-zinc-900 rounded-full p-1">
          {["day", "week", "month"].map((m) => (
            <button key={m} onClick={() => { setMode(m); setRef(m === "month" ? new Date() : startOfWeek(new Date())); }} data-testid={`mode-${m}`}
              className={`px-3 h-7 rounded-full text-xs font-medium capitalize ${mode === m ? "gold-gradient text-black" : "text-zinc-400"}`}>
              {m === "day" ? "Jour" : m === "week" ? "Semaine" : "Mois"}
            </button>
          ))}
        </div>
      </div>

      <div className={mode === "day" ? "" : `grid gap-2 ${mode === "week" ? "grid-cols-2 md:grid-cols-7" : "grid-cols-7"}`} data-testid="calendar-grid">
        {days.map((d) => {
          const di = iso(d);
          const list = byDate[di] || [];
          const isToday = di === today;
          const dim = mode === "month" && d.getMonth() !== ref.getMonth();
          return (
            <div key={di} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(di)} data-testid={`day-${di}`}
              className={`rounded-xl border p-2 min-h-[90px] ${isToday ? "border-amber-500/50 bg-amber-500/5" : "border-zinc-800 bg-[#18181E]"} ${dim ? "opacity-40" : ""}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] text-zinc-500">{DAYS[(d.getDay() + 6) % 7]} {d.getDate()}</span>
                {isToday && <span className="text-[9px] px-1.5 rounded-full bg-amber-500 text-black font-bold">·</span>}
              </div>
              <div className="space-y-1">
                {list.map((t) => {
                  const acc = priorityAccent(t.priority);
                  const done = t.status === "Terminée";
                  return (
                    <div key={t.id} draggable={!done} onDragStart={() => setDragId(t.id)} data-testid={`cal-task-${t.id}`}
                      className={`text-[11px] rounded-lg px-2 py-1 border cursor-grab ${done ? "opacity-50 line-through" : ""} ${t.overdue ? "border-red-500/50 bg-red-500/10" : "border-zinc-700 bg-zinc-900"}`}
                      style={{ borderLeft: `3px solid ${acc.color}` }} title={t.title}>
                      <p className="truncate font-medium">{t.title}</p>
                      <div className="flex items-center gap-1 text-[9px] text-zinc-500">
                        {t.time && <span>{t.time}</span>}
                        <span className="text-amber-400 font-mono">+{t.xp}</span>
                        {t.overdue && <span className="text-red-400">retard</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {creating && <CreateTaskModal init={creating} projects={projects} onClose={() => setCreating(null)} onDone={() => { setCreating(null); load(); }} />}

      {optim && (
        <Modal onClose={() => setOptim(null)} title="Plan optimisé" testid="optimize-modal">
          <p className="text-sm text-zinc-300 mb-4">{optim.message}</p>
          {optim.proposal.length === 0 ? <p className="text-sm text-zinc-500">Rien à réorganiser.</p> : (
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {optim.proposal.map((p, i) => (
                <div key={i} className="flex items-center justify-between bg-zinc-900 rounded-xl px-3 py-2 text-sm" data-testid={`optim-row-${i}`}>
                  <span className="truncate">{p.title}</span>
                  <span className="font-mono text-xs text-amber-400 shrink-0">{p.old_deadline || "—"} → {p.new_deadline}</span>
                </div>
              ))}
            </div>
          )}
          {optim.proposal.length > 0 && (
            <div className="flex gap-2 mt-4">
              <button onClick={applyOptimize} data-testid="apply-optimize" className="flex-1 h-11 rounded-xl gold-gradient text-black font-semibold flex items-center justify-center gap-2"><Check className="w-5 h-5" /> Appliquer le planning</button>
              <button onClick={() => setOptim(null)} className="px-4 h-11 rounded-xl border border-zinc-700 text-zinc-300">Annuler</button>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

function CreateTaskModal({ init, projects, onClose, onDone }) {
  const [form, setForm] = useState({ title: "", description: "", deadline: init.deadline || "", time: "", duration_min: "", priority: "normale", project_id: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/tasks", {
        ...form, project_id: form.project_id || null,
        duration_min: form.duration_min ? parseInt(form.duration_min) : null,
        time: form.time || null,
      });
      toast.success("Tâche créée !");
      onDone();
    } finally { setLoading(false); }
  };
  return (
    <Modal onClose={onClose} title="Nouvelle tâche" testid="calendar-task-modal">
      <form onSubmit={submit} className="space-y-4">
        <Input label="Titre" value={form.title} onChange={set("title")} testid="cal-task-title" required />
        <div className="grid grid-cols-2 gap-4">
          <Input label="Date" type="date" value={form.deadline} onChange={set("deadline")} testid="cal-task-date" />
          <Input label="Heure (option.)" type="time" value={form.time} onChange={set("time")} testid="cal-task-time" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-zinc-400 mb-1.5">Priorité (XP)</label>
            <select value={form.priority} onChange={(e) => set("priority")(e.target.value)} data-testid="cal-task-priority" className="w-full h-12 px-4 rounded-xl bg-zinc-900 border border-zinc-800 text-zinc-100 outline-none">
              {Object.entries(PRIORITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label} · +{v.xp} XP</option>)}
            </select>
          </div>
          <Input label="Durée (min)" type="number" value={form.duration_min} onChange={set("duration_min")} testid="cal-task-duration" />
        </div>
        <div>
          <label className="block text-xs font-medium text-zinc-400 mb-1.5">Projet</label>
          <select value={form.project_id} onChange={(e) => set("project_id")(e.target.value)} data-testid="cal-task-project" className="w-full h-12 px-4 rounded-xl bg-zinc-900 border border-zinc-800 text-zinc-100 outline-none">
            <option value="">Aucun</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
          </select>
        </div>
        <button type="submit" disabled={loading} data-testid="cal-task-submit" className="w-full h-12 rounded-xl gold-gradient text-black font-semibold flex items-center justify-center disabled:opacity-60">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Créer la tâche"}
        </button>
      </form>
    </Modal>
  );
}
